import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DEFAULT_THEME_IDS,
  ResolvedTokens,
  googleFontsHref,
  parseBookingSitePath,
  resolveTheme,
} from '@porter/shared-theme';
import {
  BookingSiteThemeResult,
  getBookingSiteTheme,
} from '../../services/bookingSite';
import { usePreviewOverrides } from './preview';

export interface BookingSiteTheme {
  tokens: ResolvedTokens;
  /** Null when the shop never chose one — loadFont applies the default. */
  fontId: string | null;
}

const FONT_LINK_ID = 'porter-theme-font';

export const BookingSiteThemeContext =
  React.createContext<BookingSiteTheme | null>(null);

export function useBookingSiteTheme() {
  return React.useContext(BookingSiteThemeContext);
}

// Only the selected family plus Inter — never all sixteen.
function loadFont(fontId: string | null) {
  // An unchosen font resolves to the Porter default, same as the resolver does.
  const resolved = fontId || DEFAULT_THEME_IDS.fontId;
  const existing = document.getElementById(
    FONT_LINK_ID,
  ) as HTMLLinkElement | null;

  // The default is already in the document head, so requesting it here would be
  // a second round trip for a family the page has. Navigating back to it drops
  // the link rather than leaving the previous shop's face loaded.
  if (resolved === DEFAULT_THEME_IDS.fontId) {
    existing?.remove();
    return;
  }

  const href = googleFontsHref(resolved);

  if (existing) {
    if (existing.href !== href) existing.href = href;
    return;
  }

  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

const THEME_STORE_PREFIX = 'porter:booking-site-theme:';

/** Last-known record per site, so a repeat visit paints without a fetch hold. */
function readStoredTheme(key: string): BookingSiteThemeResult | undefined {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object'
      ? (parsed as BookingSiteThemeResult)
      : undefined;
  } catch {
    return undefined;
  }
}

function writeStoredTheme(key: string, record: BookingSiteThemeResult) {
  try {
    localStorage.setItem(key, JSON.stringify(record));
  } catch {
    // Private mode or full storage — the next visit cold-loads again.
  }
}

/** Shared by the theme and the socials row, so both hit one cached request. */
function useBookingSiteRecord() {
  const { pathname, search } = useLocation();
  const slugs = React.useMemo(() => parseBookingSitePath(pathname), [pathname]);

  /**
   * The shop the visitor came from, same `?business=` param that locks the
   * booking form and skips the Location step. An artist can work at several
   * shops, so without it the theme falls back to a guess at their primary and
   * can change mid-flow. The API ignores it unless the artist works there.
   */
  const businessId = new URLSearchParams(search).get('business') ?? undefined;

  // The queryKey identity, JSON-encoded so a ':' in a slug cannot collide.
  const storeKey =
    THEME_STORE_PREFIX +
    JSON.stringify([
      slugs?.shop ?? null,
      slugs?.artist ?? null,
      businessId ?? null,
    ]);

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      'booking-site-theme',
      slugs?.shop ?? null,
      slugs?.artist ?? null,
      businessId ?? null,
    ],
    queryFn: async () => {
      const record = await getBookingSiteTheme({ ...slugs, businessId });
      writeStoredTheme(storeKey, record);
      return record;
    },
    enabled: !!slugs,
    staleTime: 5 * 60 * 1000,
    // The API is no-store so a publish shows up right away; the last-known
    // copy paints the page meanwhile, and age 0 makes the fresh fetch follow.
    initialData: () => (slugs ? readStoredTheme(storeKey) : undefined),
    initialDataUpdatedAt: 0,
    // Opts out of the client-wide keepPreviousData: on a key change it would
    // hand back the previous owner's row and paint their colors on this page.
    placeholderData: undefined,
  });

  // In preview the customizer drives the ids, and whichever socials it pushed;
  // the logo still comes from the saved record, refetched on its own message.
  const preview = usePreviewOverrides();
  const { data } = query;
  const merged = React.useMemo(
    () =>
      data && preview
        ? { ...data, ...(preview.ids ?? {}), ...preview.socials }
        : data,
    [data, preview],
  );

  return {
    record: merged,
    isError: query.isError,
    hasSlugs: !!slugs,
  };
}

/**
 * Socials are stored beside the theme but independently of it, so a shop that
 * never opened the customizer can still have links to render.
 */
export function useBookingSiteSocials() {
  const { record } = useBookingSiteRecord();
  if (!record) return null;

  const { instagramHandle, tiktokHandle, facebookUrl, websiteUrl } = record;
  if (!instagramHandle && !tiktokHandle && !facebookUrl && !websiteUrl) {
    return null;
  }

  return { instagramHandle, tiktokHandle, facebookUrl, websiteUrl };
}

/**
 * Theme for the current booking site page. undefined while resolving; null when
 * the page should render unthemed, either because it is not a booking site page
 * or because the theme could not be loaded.
 */
export function useResolvedBookingSiteTheme() {
  const { record: effective, isError, hasSlugs } = useBookingSiteRecord();

  // Pulled out as primitives so the memo below keys on the ids themselves.
  // Keyed on `effective`, editing a social link would re-resolve the palette
  // and, downstream of that, rebuild the MUI theme on every keystroke.
  const hasTheme = !!effective;
  const backgroundId = effective?.backgroundId ?? null;
  const primaryId = effective?.primaryId ?? null;
  const accentId = effective?.accentId ?? null;
  const themeFontId = effective?.fontId ?? null;

  const tokens = React.useMemo(
    () =>
      hasTheme
        ? resolveTheme({
            backgroundId,
            primaryId,
            accentId,
            fontId: themeFontId,
          })
        : null,
    [hasTheme, backgroundId, primaryId, accentId, themeFontId],
  );

  const theme = React.useMemo<BookingSiteTheme | undefined>(
    () =>
      effective && tokens
        ? { tokens, fontId: effective.fontId ?? null }
        : undefined,
    [effective, tokens],
  );

  React.useEffect(() => {
    if (theme) loadFont(theme.fontId);
  }, [theme]);

  if (!hasSlugs) return null;
  // A theme we cannot load should not hold the page on a spinner — but the
  // last-known copy, when there is one, still beats unthemed.
  if (isError && !effective) return null;
  // An untouched shop resolves to the Porter default theme, so the one look
  // is defined by the resolver rather than a second set of hardcoded values.
  return theme;
}
